import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { FlaskConical, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useUpdateTestOrder } from '@/features/patient-record/hooks/useTestOrderMutations';
import { patientQueries } from '@/features/patient-record/queries';
import type {
  TestOrderStatus,
  TestOrderViewModel,
} from '@/features/patient-record/types';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';

import { ClinicalSection, type ClinicalSectionLabels } from '../ClinicalSection';
import { ConfirmDeleteDialog } from '../_shared/ConfirmDeleteDialog';

import { TestOrderFormDialog } from './TestOrderForm';
import { TestOrderRow } from './TestOrderRow';
import { toTestOrderInput } from './toTestOrderInput';

interface TestOrdersSectionProps {
  readonly guid: string;
}

type StatusFilter = 'ALL' | TestOrderStatus;

const FILTER_OPTIONS: readonly StatusFilter[] = [
  'ALL',
  'ORDERED',
  'SAMPLE_SENT',
  'RESULTED',
  'REVIEWED',
  'CANCELLED',
];

const STATUS_RANK: Record<TestOrderStatus, number> = {
  ORDERED: 0,
  SAMPLE_SENT: 1,
  RESULTED: 2,
  REVIEWED: 3,
  CANCELLED: 4,
};

/** Sort: ordered_date desc, status asc as a stable secondary. */
function sortOrders(orders: readonly TestOrderViewModel[]): TestOrderViewModel[] {
  return [...orders].sort((a, b) => {
    if (a.orderedDate !== b.orderedDate) {
      return a.orderedDate < b.orderedDate ? 1 : -1;
    }
    return STATUS_RANK[a.status] - STATUS_RANK[b.status];
  });
}

export function TestOrdersSection({ guid }: TestOrdersSectionProps): ReactElement {
  const { t } = useTranslation();
  const query = useQuery(patientQueries.testOrders(guid));
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pendingCancel, setPendingCancel] = useState<TestOrderViewModel | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('ALL');
  const updateMutation = useUpdateTestOrder(guid);

  const allOrders = query.data ?? [];

  const filteredAndSorted = useMemo<TestOrderViewModel[]>(() => {
    const matching =
      filter === 'ALL' ? allOrders : allOrders.filter((o) => o.status === filter);
    return sortOrders(matching);
  }, [allOrders, filter]);

  const editing =
    editingId !== null ? allOrders.find((o) => o.id === editingId) : null;

  // Build a query result that respects the filter — ClinicalSection
  // shows its empty state when the list is empty, which is what we want
  // for filtered-to-zero too. We re-use the original query for loading
  // and error states.
  const filteredQuery = useMemo(
    () => ({
      ...query,
      data: filteredAndSorted,
    }),
    [query, filteredAndSorted],
  );

  const labels: ClinicalSectionLabels = {
    title: t('patient.testOrders.heading'),
    empty:
      filter === 'ALL'
        ? t('patient.testOrders.empty')
        : t('patient.testOrders.emptyFiltered'),
    emptyIcon: <FlaskConical className="h-5 w-5" aria-hidden="true" />,
    loading: t('patient.testOrders.loading'),
    errorHeading: t('patient.testOrders.error.heading'),
    errorMessage: t('patient.testOrders.error.message'),
    codeLabel: t('routes.patient.error.codeLabel'),
    retryLabel: t('routes.patient.error.retry'),
  };

  return (
    <>
      <ClinicalSection
        id="test-orders"
        labels={labels}
        query={filteredQuery}
        summary={allOrders.map((order) => ({
          label: order.testName,
          value: `${t(`patient.testOrders.status.${order.status}`)} · ${formatDate(order.orderedDate, { month: 'short', day: 'numeric' })}`,
          filled: true,
        }))}
        itemKey={(order) => order.id}
        renderItem={(order) => (
          <TestOrderRow
            guid={guid}
            order={order}
            onEdit={() => {
              setEditingId(order.id);
            }}
            onCancel={() => {
              setPendingCancel(order);
            }}
          />
        )}
        headerAction={
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setComposerOpen(true);
            }}
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
            {t('patient.testOrders.newOrder')}
          </Button>
        }
        toolbar={
          <div
            className="flex flex-wrap items-center gap-1.5"
            role="tablist"
            aria-label={t('patient.testOrders.filterLabel')}
          >
            {FILTER_OPTIONS.map((option) => {
              const isActive = filter === option;
              const count =
                option === 'ALL'
                  ? allOrders.length
                  : allOrders.filter((o) => o.status === option).length;
              return (
                <button
                  key={option}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => {
                    setFilter(option);
                  }}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card text-muted-foreground border-border hover:bg-muted/60',
                  )}
                >
                  <span>
                    {option === 'ALL'
                      ? t('patient.testOrders.filter.ALL')
                      : t(`patient.testOrders.status.${option}`)}
                  </span>
                  <span className="tabular-nums">{count}</span>
                </button>
              );
            })}
          </div>
        }
      />
      {composerOpen && (
        <TestOrderFormDialog
          guid={guid}
          mode="add"
          onClose={() => {
            setComposerOpen(false);
          }}
        />
      )}
      {editing !== undefined && editing !== null && (
        <TestOrderFormDialog
          guid={guid}
          mode="edit"
          order={editing}
          onClose={() => {
            setEditingId(null);
          }}
        />
      )}
      {pendingCancel !== null && (
        <ConfirmDeleteDialog
          title={t('patient.testOrders.cancelConfirmTitle')}
          message={t('patient.testOrders.cancelConfirmMessage')}
          confirmLabel={t('patient.testOrders.cancelConfirmLabel')}
          busy={updateMutation.isPending}
          onCancel={() => {
            setPendingCancel(null);
          }}
          onConfirm={() => {
            updateMutation.mutate(
              {
                id: pendingCancel.id,
                input: toTestOrderInput(pendingCancel, { status: 'CANCELLED' }),
              },
              {
                onSettled: () => {
                  setPendingCancel(null);
                },
              },
            );
          }}
        />
      )}
    </>
  );
}
